/* eslint-disable */
import { RGBAFormat, RGBFormat } from 'three/src/constants.js';
import { Clock } from 'three/src/core/Clock';
import { Vector4 } from 'three/src/math/Vector4.js';
import { Texture } from 'three/src/textures/Texture';
import { TextureLoader } from 'three/src/loaders/TextureLoader';
import { Scene } from 'three/src/scenes/Scene';
import { WebGLRenderer } from 'three/src/renderers/WebGLRenderer';
import { PerspectiveCamera } from 'three/src/cameras/PerspectiveCamera';
import { PlaneGeometry } from 'three/src/geometries/PlaneGeometry';
import { ShaderMaterial } from 'three/src/materials/ShaderMaterial';
import { Mesh } from 'three/src/objects/Mesh';
import { TweenMax, Power2 } from 'gsap/all';
import { requestAnimationFrame, cancelAnimationFrame } from '~/utils/animation/animationFrame';
import disposeThreeObject from '~/utils/three/disposeThreeObject';
import getRatio from '~/utils/three/getRatio';
import unproxyAll from '~/utils/proto/unproxyAll';
import proxyAll from '~/utils/proto/proxyAll';
import createImageLoader, { normalizeAssets } from '@dcode/loader/createImageLoader';
import getElementHeight from '~/utils/dom/getElementHeight';
import getElementWidth from '~/utils/dom/getElementWidth';
import clamp from '~/utils/number/clamp';

export const utils = {
	getRatio,
};

export class GLSL {
	constructor(config) {
		proxyAll(this);

		this.config = config;
		this.paused = true;
		this.textures = [];
		this.clock = new Clock();
		this.loader = new TextureLoader();

		this.scene = new Scene();

		const rendererWidth = getElementWidth(this.config.sceneElement, true, true);
		const rendererHeight = getElementHeight(this.config.sceneElement, true, true);
		const rendererColor = 0xEEEEEE;
		const rendererAlpha = 1;
		this.renderer = new WebGLRenderer();
		this.renderer.setPixelRatio(window.devicePixelRatio);
		this.renderer.setSize(rendererWidth, rendererHeight);
		this.renderer.setClearColor(rendererColor, rendererAlpha);

		const cameraFov = 70;
		const cameraAspect = rendererWidth / rendererHeight;
		const cameraNear = 0.001;
		const cameraFar = 600;
		const cameraX = 0;
		const cameraY = 0;
		const cameraZ = 2;
		this.camera = new PerspectiveCamera(cameraFov, cameraAspect, cameraNear, cameraFar);
		this.camera.position.set(cameraX, cameraY, cameraZ);

		this.config.sceneElement.appendChild(this.renderer.domElement);

		this.loadTextures(this.config.imageList);
	}

	isJPEG(url) {
		return url.search(/^data\:image\/jpeg/) === 0
		|| url.search(/\.jpe?g(?:$|\?)/i) > 0
	}

	makeTexture({ element:img }) {
		const texture = new Texture();
		texture.center.set(0.5, 0.5);
		texture.format = this.isJPEG(img.src) ? RGBFormat : RGBAFormat;
		texture.needsUpdate = true;
		texture.image = img;
		return texture;
	}

	loadTextures(assets) {
		this.disposeTextureLoader = createImageLoader({
			assets,
			onStart: this.config.onLoadStart,
			onError: this.config.onLoadError,
			onProgress: this.config.onLoadProgress,
			onComplete: (response) => this
				.setTextures(response.list())
				.then(this.attachObjects)
				.then(this.attachEvents)
				.then(this.resize)
				.then(this.play)
				.then(this.config.onLoadComplete)
			,
		});
	}

	setTextures(assets) {
		this.textures = [];
		const promises = normalizeAssets(assets).reduce((promises, asset, id) => {
			const promise = new Promise((resolve) => {
				const texture = this.loader.load(asset.src, resolve);
				texture.center.set(0.5, 0.5);
				this.textures[id] = texture;
			});
			promises.push(promise);
			return promises;
		}, []);
		return Promise.all(promises);
	}

	attachEvents() {
		window.addEventListener('resize', this.resize);
	}

	detachEvents() {
		window.removeEventListener('resize', this.resize);
	}

	attachObjects() {
		const uniforms = this.config.material.uniforms;
		delete this.config.material.uniforms;
		this.geometry = new PlaneGeometry(1, 1, 2, 2);
		this.material = new ShaderMaterial({
			side: 2,
			fragmentShader: this.config.material.fragmentShader,
			vertexShader: this.config.material.vertexShader,
			transparent: this.config.material.transparent,
			extensions: this.config.material.extensions,
			defines: this.config.material.defines,
			uniforms: uniforms(this, new Vector4(), utils),
		});
		this.mesh = new Mesh(this.geometry, this.material);
		this.scene.add(this.mesh);
	}

	resize() {
		if (!this || !this.renderer || !this.material) return;

		const rendererWidth = this.config.sceneElement.offsetWidth;
		const rendererHeight = this.config.sceneElement.offsetHeight;

		this.renderer.setSize(rendererWidth, rendererHeight);
		this.camera.aspect = rendererWidth / rendererHeight;

		let resolutionZ, resolutionW;
		const currentTexture = this.textures[0];
		const textureAspect = currentTexture.image.height / currentTexture.image.width;
		if (rendererHeight / rendererWidth > textureAspect) {
			resolutionZ = (rendererWidth / rendererHeight) * textureAspect;
			resolutionW = 1;
		} else {
			resolutionZ = 1;
			resolutionW = (rendererHeight / rendererWidth) / textureAspect;
		}
		this.material.uniforms.u_resolution.value.x = rendererWidth;
		this.material.uniforms.u_resolution.value.y = rendererHeight;
		this.material.uniforms.u_resolution.value.z = resolutionZ;
		this.material.uniforms.u_resolution.value.w = resolutionW;

		const height = 1;
		const distance = this.camera.position.z;
		this.camera.fov = 2 * (180 / Math.PI) * Math.atan(height / (2 * distance));
		this.mesh.scale.x = this.camera.aspect;
		this.mesh.scale.y = 1;
		this.camera.updateProjectionMatrix();
	}

	setProgress(progress, time = 0.4, props = {}) {
		const uniforms = !!this.material && this.material.uniforms;
		if (!uniforms || !uniforms.u_progressHover) return;
		TweenMax.to(uniforms.u_progressHover, time, {
			value: clamp(progress, 0, 1),
			ease: Power2.easeInOut,
			...props,
		});
	}

	next() {
		if (this.isRunning) return;
		else this.isRunning = true;
		const size = this.textures.length;
		const nextTexture = this.textures[(this.current + 1) % size];
		this.material.uniforms.u_texture2.value = nextTexture;
		TweenMax.to(this.material.uniforms.u_progressHover, this.duration, {
			value: 1,
			ease: Power2.easeInOut,
			onComplete: () => {
				this.current = (this.current + 1) % size;
				this.material.uniforms.u_texture1.value = nextTexture;
				this.material.uniforms.u_progressHover.value = 0;
				delete this.isRunning;
			},
		});
	}

	play() {
		this.paused = false;
		this.render();
	}

	pause() {
		this.paused = true;
	}

	stop() {
		if (this.rafId) cancelAnimationFrame(this.rafId);
		delete this.rafId;
	}

	animate() {
		if (!this || !this.material || !this.renderer || this.paused) return;
		this.material.uniforms.u_time.value += this.clock.getDelta();
		this.renderer.render(this.scene, this.camera);
		this.render();
	}

	render() {
		this.rafId = requestAnimationFrame(this.animate);
	}

	dispose() {
		this.disposeTextureLoader && this.disposeTextureLoader();
		this.detachEvents();
		disposeThreeObject(this.scene);
		this.renderer.renderLists.dispose();
		this.renderer.dispose();
		this.renderer.domElement.parentElement.removeChild(this.renderer.domElement);
		this.renderer.domElement = null;
		this.renderer = null;
		this.stop();
	}
}

export default GLSL;
